import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { apiClient, APIError } from '@/utils/api-client';

export type AccountType =
  'asset' | 'liability' | 'equity' | 'income' | 'expense';

export interface ChartOfAccount {
  id: string;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  description?: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ChartOfAccountsListResponse {
  status: string;
  items: ChartOfAccount[];
  total: number;
  page: number;
  page_size: number;
}

interface ChartOfAccountsGroupedResponse {
  status: string;
  assets: ChartOfAccount[];
  liabilities: ChartOfAccount[];
  equity: ChartOfAccount[];
  income: ChartOfAccount[];
  expenses: ChartOfAccount[];
  total: number;
}

interface ChartOfAccountCreate {
  account_code: string;
  account_name: string;
  account_type: AccountType;
  description?: string;
  display_order?: number;
}

interface ChartOfAccountUpdate {
  account_name?: string;
  description?: string;
  display_order?: number;
}

interface CodeAvailabilityResponse {
  status: string;
  data: {
    available: boolean;
    message: string;
  };
}

// Get paginated chart of accounts
export function useChartOfAccounts(params?: {
  page?: number;
  page_size?: number;
  account_type?: AccountType;
}) {
  return useApiQuery<ChartOfAccountsListResponse>(
    ['chartOfAccounts', params],
    () =>
      apiClient.get<ChartOfAccountsListResponse>('/admin/chart-of-accounts', {
        params,
      }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
}

// Get accounts grouped by type
export function useChartOfAccountsGrouped(options?: { enabled?: boolean }) {
  const url = `/admin/chart-of-accounts/grouped`;

  return useApiQuery<ChartOfAccountsGroupedResponse>(
    ['chartOfAccountsGrouped'],
    () => apiClient.get<ChartOfAccountsGroupedResponse>(url),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: options?.enabled ?? true,
    }
  );
}

// Check code availability
export function useCheckAccountCode(code: string, excludeId?: string) {
  const url = excludeId
    ? `/admin/chart-of-accounts/check-code/${encodeURIComponent(code)}?exclude_id=${excludeId}`
    : `/admin/chart-of-accounts/check-code/${encodeURIComponent(code)}`;

  return useApiQuery<
    CodeAvailabilityResponse,
    unknown,
    { available: boolean; message: string }
  >(
    ['accountCodeAvailability', code, excludeId],
    () => apiClient.get<CodeAvailabilityResponse>(url),
    {
      enabled: !!code && code.length > 0,
      staleTime: 30 * 1000, // 30 seconds
      select: (response) => response.data,
    }
  );
}

// Create account
export function useCreateChartOfAccount() {
  return useApiMutation<ChartOfAccount, APIError, ChartOfAccountCreate>(
    (data) => apiClient.post('/admin/chart-of-accounts', data),
    {
      mutationKey: ['createChartOfAccount'],
      invalidateKeys: [['chartOfAccounts'], ['chartOfAccountsGrouped']],
      successMessage: 'Account created successfully',
    }
  );
}

// Update account
export function useUpdateChartOfAccount() {
  return useApiMutation<
    ChartOfAccount,
    APIError,
    { id: string; data: ChartOfAccountUpdate }
  >(({ id, data }) => apiClient.put(`/admin/chart-of-accounts/${id}`, data), {
    mutationKey: ['updateChartOfAccount'],
    invalidateKeys: [['chartOfAccounts'], ['chartOfAccountsGrouped']],
    successMessage: 'Account updated successfully',
  });
}

// Delete account
export function useDeleteChartOfAccount() {
  return useApiMutation<void, APIError, string>(
    (id) => apiClient.delete(`/admin/chart-of-accounts/${id}`),
    {
      mutationKey: ['deleteChartOfAccount'],
      invalidateKeys: [['chartOfAccounts'], ['chartOfAccountsGrouped']],
      successMessage: 'Account deleted successfully',
    }
  );
}
